// Accelerator playbook — the 10-step "idea → launched startup" arc that
// powers /accelerator. Each step becomes one assistant turn in the
// chat shell when the user clicks "Start the Accelerator".
//
// Why a single linear playbook (not a hub of separate ones): the user
// asked for "one long playbook" so a founder can sit down, hit start,
// and walk through the full path without choosing where to begin.
//
// The chat shell consumes this via stashChatSeed → the primer is a
// markdown list of all 10 steps so the model has the full arc as
// context, and the draft is the first prompt the founder sends.
//
// Source: ported from conceptually.com (sister Lovable project) and
// adapted to fit HackerLabs's chat-first surface.

export interface AcceleratorStep {
  number: number;
  title: string;
  goal: string;
  /** What the user actually asks the AI to produce in this step. */
  ask: string;
  /** Short blurb shown on the landing page card. */
  blurb: string;
  /** Lucide icon name — resolved in the consuming component. */
  icon:
    | "Sparkles"
    | "Search"
    | "Code2"
    | "FileText"
    | "DollarSign"
    | "Users"
    | "Megaphone"
    | "BarChart3"
    | "Rocket"
    | "TrendingUp";
}

export const ACCELERATOR_STEPS: AcceleratorStep[] = [
  {
    number: 1,
    title: "Validate the idea",
    goal: "Pressure-test the problem, the customer, and the willingness to pay.",
    ask: "Help me validate my startup idea. Ask me 5 sharp questions about the problem, the customer, and proof of demand. Then score the idea 1–10 with reasoning.",
    blurb: "AI feasibility analysis: TAM/SAM/SOM, competitive read, demand signals.",
    icon: "Sparkles",
  },
  {
    number: 2,
    title: "Research the market",
    goal: "Map competitors, alternatives, and the real wedge.",
    ask: "Research the market for my idea. Identify the top 5 competitors / alternatives, what they do well, where they're weak, and the specific wedge I should attack first.",
    blurb: "Competitive landscape, pricing benchmarks, and the wedge to attack first.",
    icon: "Search",
  },
  {
    number: 3,
    title: "Define the MVP",
    goal: "Carve the smallest thing that proves the wedge.",
    ask: "Help me scope an MVP that proves my wedge in 2–4 weeks. Give me: (1) the single user job, (2) the 3 must-have features, (3) the 5 things I will NOT build yet.",
    blurb: "Smallest-viable scope, 3 must-have features, 5 cuts, 4-week build plan.",
    icon: "Code2",
  },
  {
    number: 4,
    title: "Build it in Lovable",
    goal: "Generate a copy-paste prompt that builds the MVP in Lovable.",
    ask: "Write me a single, copy-paste-ready Lovable build prompt for my MVP. Include: tech stack hints, the data model, the key screens, and the auth + payments setup.",
    blurb: "Copy-paste Lovable prompt with stack, data model, screens, auth + payments.",
    icon: "Code2",
  },
  {
    number: 5,
    title: "Write the business plan",
    goal: "One-pager: problem, solution, market, model, traction, ask.",
    ask: "Draft a one-page business plan for my startup with sections: Problem, Solution, Market (TAM/SAM/SOM), Business Model, Traction, Team, Ask. Keep each section under 80 words.",
    blurb: "One-pager investors actually read: problem, solution, model, traction, ask.",
    icon: "FileText",
  },
  {
    number: 6,
    title: "Set pricing & revenue model",
    goal: "Pick a model, set tiers, project Year 1.",
    ask: "Recommend a pricing + revenue model for my startup. Give me 3 tier options with prices, the rationale, and a Year 1 revenue projection at conservative / realistic / aggressive adoption.",
    blurb: "3 tier options, rationale, and Year 1 conservative/realistic/aggressive projections.",
    icon: "DollarSign",
  },
  {
    number: 7,
    title: "Find a mentor",
    goal: "Identify the right kind of operator + how to cold-reach them.",
    ask: "Help me find the right mentor for this stage. (1) Profile the ideal mentor (background, stage they've shipped through). (2) Suggest 5 places to find them. (3) Write a 4-line cold outreach message I can send today.",
    blurb: "Mentor profile, 5 sourcing channels, copy-paste cold outreach script.",
    icon: "Users",
  },
  {
    number: 8,
    title: "Build the GTM playbook",
    goal: "Pick the launch channel and the first 100 customer plan.",
    ask: "Build my go-to-market playbook. Pick the single best launch channel for my product, then write the 30-day plan to get the first 100 paying customers from that channel.",
    blurb: "Single launch channel + 30-day plan for the first 100 paying customers.",
    icon: "Megaphone",
  },
  {
    number: 9,
    title: "Define traction metrics",
    goal: "The 3–5 numbers that prove this is working.",
    ask: "Define the 3–5 metrics I should track weekly to prove traction at this stage. For each: the definition, the target after 30/60/90 days, and what to do if I miss the target.",
    blurb: "3–5 weekly metrics, 30/60/90-day targets, and the playbook if you miss.",
    icon: "BarChart3",
  },
  {
    number: 10,
    title: "Launch & grow",
    goal: "Ship publicly + the next-90-day growth motion.",
    ask: "Write my launch plan: (1) the 7-day pre-launch checklist, (2) the launch-day post for X/LinkedIn/PH, (3) the 90-day growth motion that compounds week over week.",
    blurb: "Launch checklist, the launch-day post, and the 90-day compounding growth motion.",
    icon: "Rocket",
  },
];

/** Modules shown as cards on the landing page (matches phases on
 *  conceptually.com but expanded to one card per playbook step). */
export const ACCELERATOR_MODULES = ACCELERATOR_STEPS;

/** Perks block — verbatim from conceptually.com so the value prop
 *  ($100K+ in startup tools) ports cleanly. */
export const ACCELERATOR_PERKS: { label: string; value: string }[] = [
  { label: "AWS", value: "$100K" },
  { label: "Google Cloud", value: "$200K" },
  { label: "Stripe", value: "$5K+" },
  { label: "OpenAI", value: "$2,500" },
  { label: "HubSpot", value: "90% off" },
  { label: "Figma", value: "Free" },
];

export const ACCELERATOR_TESTIMONIALS: {
  quote: string;
  name: string;
  role: string;
}[] = [
  {
    quote:
      "Walked in with a half-formed idea. Walked out 3 weeks later with a paying MVP and a pitch deck I'd actually show an investor.",
    name: "Maya R.",
    role: "Solo founder · b2b SaaS",
  },
  {
    quote:
      "The Lovable build prompt alone saved me ~40 hours. Step 4 dropped a working MVP I shipped to my first 10 customers the same week.",
    name: "Jordan K.",
    role: "Indie hacker",
  },
  {
    quote:
      "The mentor playbook is criminally underrated. Sent the cold-outreach template to 12 people, landed 4 calls, found my advisor.",
    name: "Priya N.",
    role: "Pre-seed founder",
  },
];

export const ACCELERATOR_FAQ: { q: string; a: string }[] = [
  {
    q: "How long does the Accelerator take?",
    a: "Most founders complete the 10 steps in 2–4 weeks of focused work. The chat saves automatically so you can pause between steps.",
  },
  {
    q: "Do I need a technical co-founder?",
    a: "No. Step 4 generates a copy-paste Lovable prompt that builds your MVP for you — you'll have a working app without writing code.",
  },
  {
    q: "Is this just a chatbot?",
    a: "No. Each step is a structured prompt designed by founders, sent to the right model for the job (reasoning vs. fast generation), and grounded in your specific idea.",
  },
  {
    q: "What does it cost?",
    a: "The 10-step playbook is free. AI usage is metered against your HackerLabs plan — most founders complete the full arc inside the free monthly credits.",
  },
  {
    q: "Can I share my outputs?",
    a: "Yes. Every chat is auto-saved with a smart title (e.g. 'Quest: Validate idea · Apr 27 14:32') and you can export the full transcript as Markdown from the Recent chats list.",
  },
];

/** Build the chat seed primer — a single markdown blob that gives the
 *  model the full 10-step arc as context, plus the founder's first
 *  ask. Consumed by stashChatSeed → UnifiedChatShell. */
export function buildAcceleratorSeed(): {
  primer: string;
  draft: string;
} {
  const primer = [
    "# 🚀 HackerLabs Startup Accelerator",
    "",
    "You are coaching a founder through the 10-step Accelerator playbook.",
    "Be sharp, specific, and bias toward shippable next actions over theory.",
    "Use the founder's actual idea (they'll share it next) — never give generic advice.",
    "",
    "## The 10 steps",
    "",
    ...ACCELERATOR_STEPS.map(
      (s) => `${s.number}. **${s.title}** — ${s.goal}`,
    ),
    "",
    "Start with **Step 1: Validate the idea**. After each step, ask if they want to continue to the next one.",
  ].join("\n");

  const draft =
    "I want to start the Accelerator. My startup idea is: [describe your idea in 1–2 sentences]. Let's begin with Step 1 — validate it.";

  return { primer, draft };
}
